import { createFileRoute } from "@tanstack/react-router";

export const Route = createFileRoute(
  "/admin/_layout/inventory/unit-conversions"
)({
  component: UnitConversionsPage,
});

import { useState } from "react";
import { toast } from "sonner";
import { SidebarInset } from "@/components/ui/sidebar";
import { DashboardHeader } from "@/components/dashboard-header";
import PageHeader from "@/components/reusable-components/header/page-header";
import { PaginationComponent } from "@/components/crud/Pagination/Pagination";
import { PAGE_SIZE_OPTIONS } from "@/lib/constants";
import { usePaginationState } from "@/hooks/ui/usePaginationState";
import { DeleteConfirmDialog } from "@/components/crud/DeleteConfirmDialog/DeleteConfirmDialog";
import {
  useFetchUnitConversions,
  useUnitConversionMutations,
  useFetchUnits,
  ALL_ACTIVE_PARAMS,
} from "@/hooks/api/inventory/useCatalog";
import { UnitConversionTable } from "@/components/inventory/tables/UnitConversionTable";
import { UnitConversionFormSheet } from "@/components/inventory/forms/UnitConversionFormSheet";
import type { UnitConversion, UnitConversionPayload } from "@/types/inventory";

function UnitConversionsPage() {
  const { page, setPage, pageSize, handlePageSizeChange } =
    usePaginationState();

  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [isViewOnly, setIsViewOnly] = useState(false);
  const [editing, setEditing] = useState<UnitConversion | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<UnitConversion | null>(
    null
  );

  // QUERIES & MUTATIONS
  const conversionsQuery = useFetchUnitConversions({
    page,
    page_size: pageSize,
  });
  const conversions = conversionsQuery.data?.results ?? [];
  const count = conversionsQuery.data?.count ?? 0;

  const unitsQuery = useFetchUnits(ALL_ACTIVE_PARAMS);
  const units = unitsQuery.data?.results ?? [];

  const { createConversion, updateConversion, deleteConversion } =
    useUnitConversionMutations();

  const isMutating =
    createConversion.isPending ||
    updateConversion.isPending ||
    deleteConversion.isPending;
  const isLoading = conversionsQuery.isPending || isMutating;
  const isFetching = conversionsQuery.isFetching || isMutating;

  const handleSubmit = async (data: UnitConversionPayload) => {
    if (editing) {
      await updateConversion.mutateAsync({ id: editing.id, payload: data });
      toast.success("Conversion updated");
    } else {
      await createConversion.mutateAsync(data);
      toast.success("Conversion created");
    }
    setIsSheetOpen(false);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    try {
      await deleteConversion.mutateAsync({ id: deleteTarget.id });
      toast.success("Conversion deleted");
      setDeleteTarget(null);
    } catch {
      // error toast comes from the mutation's meta.errorMessage
    }
  };

  return (
    <SidebarInset>
      <DashboardHeader />
      <div className="space-y-4 p-6 pt-0">
        <PageHeader
          title="Unit Conversions"
          description="Define conversion factors between units"
          onAdd={() => {
            setEditing(null);
            setIsViewOnly(false);
            setIsSheetOpen(true);
          }}
          addLabel="Add Conversion"
          isLoading={isFetching}
        />
        <UnitConversionTable
          conversions={conversions}
          units={units}
          isLoading={isLoading}
          onView={(c) => {
            setEditing(c);
            setIsViewOnly(true);
            setIsSheetOpen(true);
          }}
          onEdit={(c) => {
            setEditing(c);
            setIsViewOnly(false);
            setIsSheetOpen(true);
          }}
          onDelete={(c) => setDeleteTarget(c)}
        />
        <PaginationComponent
          count={count}
          page={page}
          onPageChange={setPage}
          isLoading={isFetching}
          pageSize={pageSize}
          onPageSizeChange={handlePageSizeChange}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
        />
        <UnitConversionFormSheet
          isOpen={isSheetOpen}
          onClose={() => setIsSheetOpen(false)}
          onSubmit={handleSubmit}
          editing={editing}
          units={units}
          isLoading={isMutating}
          isViewOnly={isViewOnly}
        />
        <DeleteConfirmDialog
          isOpen={!!deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onConfirm={handleDeleteConfirm}
          title="Delete Conversion"
          description="Delete this unit conversion?"
          itemName={String(deleteTarget?.id ?? "")}
          isLoading={isMutating}
          destructive
        />
      </div>
    </SidebarInset>
  );
}
